import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'input',
    'listbox',
    'chipContainer',
    'hiddenFields',
    'option',
  ]
  static values = {
    selected: Array,
    createUrl: String,
  }

  connect() {
    this.#syncFromSelected()
    this.#handleFocusOutside = this.#onFocusOutside.bind(this)
    document.addEventListener('focusin', this.#handleFocusOutside)
  }

  disconnect() {
    document.removeEventListener('focusin', this.#handleFocusOutside)
  }

  // --- Input actions ---

  filter() {
    const query = this.inputTarget.value.toLowerCase().trim()

    this.optionTargets.forEach((option) => {
      if (this.selectedValue.includes(option.dataset.value)) {
        option.hidden = true
        return
      }

      if (!query) {
        option.hidden = false
        return
      }

      const filterable = option.dataset.filterableAs.toLowerCase()
      option.hidden = !filterable.includes(query)
    })

    this.#updateCreateOption(query)
    this.#selectFirst()
    this.open()
  }

  navigate(event) {
    if (!this.#isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        this.open()
      }
      return
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.#moveSelection(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.#moveSelection(-1)
        break
      case 'Enter':
        event.preventDefault()
        this.#confirmSelection()
        break
      case 'Tab':
        this.#confirmSelection()
        this.close()
        break
      case 'Escape':
        event.preventDefault()
        this.inputTarget.value = ''
        this.close()
        this.#filterOptions()
        break
      case 'Backspace':
        if (this.inputTarget.value === '' && this.selectedValue.length > 0) {
          this.#removeLastChip()
        }
        break
    }
  }

  toggle() {
    this.#isOpen ? this.close() : this.open()
  }

  // --- Option actions ---

  selectOption(event) {
    const option = event.currentTarget
    const value = option.dataset.value
    this.#addSelection(value)
  }

  async createOption(event) {
    event.preventDefault()
    const name = this.inputTarget.value.trim().toLowerCase()
    if (!name) return

    try {
      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("[name='csrf-token']").content,
        },
        body: JSON.stringify({ name }),
      })

      if (!response.ok) return

      const tag = await response.json()
      this.#insertOption(tag)
      this.#addSelection(String(tag.id))
    } catch {
      // noop
    }
  }

  // --- Chip actions ---

  removeChip(event) {
    event.stopPropagation()
    const value = event.currentTarget.dataset.value
    this.selectedValue = this.selectedValue.filter((v) => v !== value)
    this.#syncFromSelected()
  }

  focusInput() {
    this.inputTarget.focus()
  }

  // --- Open/close ---

  open() {
    this.listboxTarget.hidden = false
    this.inputTarget.setAttribute('aria-expanded', 'true')
  }

  close() {
    this.listboxTarget.hidden = true
    this.inputTarget.setAttribute('aria-expanded', 'false')
    this.#clearActiveDescendant()
  }

  // --- Private ---

  #handleFocusOutside

  #addSelection(value) {
    if (this.selectedValue.includes(value)) return

    this.selectedValue = [...this.selectedValue, value]
    this.inputTarget.value = ''
    this.#syncFromSelected()
    this.inputTarget.focus()
  }

  #removeLastChip() {
    const last = this.selectedValue[this.selectedValue.length - 1]
    if (!last) return

    this.selectedValue = this.selectedValue.slice(0, -1)
    this.#syncFromSelected()
  }

  #syncFromSelected() {
    this.#renderChips()
    this.#renderHiddenFields()
    this.#filterOptions()
  }

  #filterOptions() {
    const query = this.inputTarget.value.toLowerCase().trim()

    this.optionTargets.forEach((option) => {
      if (this.selectedValue.includes(option.dataset.value)) {
        option.hidden = true
        return
      }

      if (!query) {
        option.hidden = false
        return
      }

      const filterable = option.dataset.filterableAs.toLowerCase()
      option.hidden = !filterable.includes(query)
    })

    this.#updateCreateOption(query)
    this.#selectFirst()
  }

  #renderChips() {
    const chips = this.selectedValue
      .map((value) => {
        const option = this.optionTargets.find((o) => o.dataset.value === value)
        if (!option) return ''

        const name = option.dataset.filterableAs
        const color = option.dataset.color

        return `
        <span class="badge badge-sm gap-1 cursor-pointer"
              style="border-color: ${color}; color: ${color}; background: transparent;"
              data-action="click->tag-combobox#removeChip"
              data-value="${value}">
          ${name} <span aria-label="Remove ${name}">&times;</span>
        </span>`
      })
      .join('')

    this.chipContainerTarget.innerHTML = chips
    this.inputTarget.placeholder =
      this.selectedValue.length > 0 ? '' : 'Add tags...'
  }

  #renderHiddenFields() {
    this.hiddenFieldsTarget.innerHTML = this.selectedValue
      .map(
        (id) => `<input type="hidden" name="block[tag_ids][]" value="${id}" />`,
      )
      .join('')
  }

  #insertOption(tag) {
    const li = document.createElement('li')
    li.id = `tag-option-${tag.id}`
    li.role = 'option'
    li.tabIndex = -1
    li.dataset.tagComboboxTarget = 'option'
    li.dataset.action = 'click->tag-combobox#selectOption'
    li.dataset.value = String(tag.id)
    li.dataset.filterableAs = tag.name
    li.dataset.color = tag.color
    li.setAttribute('aria-selected', 'false')
    li.className =
      'flex items-center gap-2 text-sm px-3 py-2 cursor-pointer rounded'
    li.innerHTML = `<span class="size-2 rounded-full shrink-0" style="background: ${tag.color}"></span>${tag.name}`

    const createLi = this.listboxTarget.querySelector('[data-create-option]')
    if (createLi) {
      this.listboxTarget.insertBefore(li, createLi)
    } else {
      this.listboxTarget.appendChild(li)
    }
  }

  #updateCreateOption(query) {
    const createLi = this.listboxTarget.querySelector('[data-create-option]')
    if (!createLi) return

    const exactMatch = this.optionTargets.some(
      (o) => o.dataset.filterableAs.toLowerCase() === query,
    )

    if (query && !exactMatch) {
      createLi.hidden = false
      createLi.querySelector('[data-create-label]').textContent =
        `Create "${query}"`
    } else {
      createLi.hidden = true
    }
  }

  // --- Listbox navigation ---

  #visibleOptions() {
    return this.optionTargets.filter((o) => !o.hidden)
  }

  #selectedOption() {
    return this.listboxTarget.querySelector("[aria-selected='true']")
  }

  #selectFirst() {
    this.#clearActiveDescendant()
    const first = this.#visibleOptions()[0]
    if (first) this.#markSelected(first)
  }

  #moveSelection(direction) {
    const visible = this.#visibleOptions()
    const createLi = this.listboxTarget.querySelector(
      '[data-create-option]:not([hidden])',
    )
    const all = createLi ? [...visible, createLi] : visible
    if (all.length === 0) return

    const current =
      this.#selectedOption() ||
      (createLi?.hasAttribute('aria-selected') && createLi)
    const currentIndex = all.indexOf(current)
    let nextIndex = currentIndex + direction

    if (nextIndex < 0) nextIndex = all.length - 1
    if (nextIndex >= all.length) nextIndex = 0

    this.#clearActiveDescendant()
    this.#markSelected(all[nextIndex])
  }

  #confirmSelection() {
    const selected =
      this.#selectedOption() ||
      this.listboxTarget.querySelector(
        "[data-create-option][aria-selected='true']",
      )
    if (selected) selected.click()
  }

  #markSelected(option) {
    option.setAttribute('aria-selected', 'true')
    option.classList.add('bg-base-200')
    this.inputTarget.setAttribute('aria-activedescendant', option.id)
    option.scrollIntoView({ block: 'nearest' })
  }

  #clearActiveDescendant() {
    this.listboxTarget
      .querySelectorAll("[aria-selected='true']")
      .forEach((o) => {
        o.setAttribute('aria-selected', 'false')
        o.classList.remove('bg-base-200')
      })
    this.inputTarget.removeAttribute('aria-activedescendant')
  }

  #onFocusOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  get #isOpen() {
    return !this.listboxTarget.hidden
  }
}
